import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Users, Copy, Building2, LogOut, Lock } from "lucide-react";
import { toast } from "sonner";
import { getUser, clearAuth, authFetch } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { PERSONA_META, useLang, LangToggle } from "@/components/landing/i18n";

const inputCls = "w-full rounded-2xl border border-ink/15 bg-white px-5 py-3.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";

const TEAM_UI = {
  en: {
    signOut: "Sign out",
    gateTitle: "Sign in to your team space",
    gateBtn: "Sign in / Create free account",
    createTitle: "Create an organization",
    createSub: "Get an invite code your team uses to join. You'll see everyone's professional reports.",
    orgNamePh: "Organization name",
    createBtn: "Create & get invite code",
    joinTitle: "Join with a code",
    joinSub: "Your HR or team lead shared a 6-character code? Enter it here.",
    joinBtn: "Join organization",
    members: (n) => `${n} member${n === 1 ? "" : "s"}`,
    proReports: (n) => `${n} professional report${n === 1 ? "" : "s"}`,
    inviteCode: "Invite code:",
    cohort: "Cohort — professional battery",
    cols: ["Member", "Industry", "Persona", "Aptitude", "Completed", "Card"],
    owner: "Owner",
    notTaken: "Not taken",
    view: "View",
    created: (code) => `Organization created — share code ${code}`,
    joined: (name) => `Joined ${name}`,
    createFail: "Could not create organization",
    joinFail: "Could not join",
    copied: "Invite code copied",
    copyFail: "Could not copy",
    dateLocale: "en-GB",
  },
  hi: {
    signOut: "साइन आउट",
    gateTitle: "अपने टीम स्पेस में साइन इन करें",
    gateBtn: "साइन इन / मुफ़्त खाता बनाएँ",
    createTitle: "संगठन बनाएँ",
    createSub: "एक आमंत्रण कोड पाएँ जिससे आपकी टीम जुड़ेगी। आप सबकी प्रोफेशनल रिपोर्ट देख पाएँगे।",
    orgNamePh: "संगठन का नाम",
    createBtn: "बनाएँ और आमंत्रण कोड पाएँ",
    joinTitle: "कोड से जुड़ें",
    joinSub: "आपके HR या टीम लीड ने 6-अंकों का कोड दिया है? यहाँ डालें।",
    joinBtn: "संगठन से जुड़ें",
    members: (n) => `${n} सदस्य`,
    proReports: (n) => `${n} प्रोफेशनल रिपोर्ट`,
    inviteCode: "आमंत्रण कोड:",
    cohort: "समूह — प्रोफेशनल बैटरी",
    cols: ["सदस्य", "उद्योग", "पर्सोना", "योग्यता", "पूर्ण", "कार्ड"],
    owner: "मालिक",
    notTaken: "नहीं दिया",
    view: "देखें",
    created: (code) => `संगठन बन गया — कोड ${code} साझा करें`,
    joined: (name) => `${name} से जुड़ गए`,
    createFail: "संगठन नहीं बन सका",
    joinFail: "जुड़ नहीं सके",
    copied: "आमंत्रण कोड कॉपी हुआ",
    copyFail: "कॉपी नहीं हो सका",
    dateLocale: "hi-IN",
  },
};

export default function TeamPage() {
  const { lang } = useLang();
  const tui = TEAM_UI[lang] || TEAM_UI.en;
  const [user, setUser] = useState(getUser());
  const [authOpen, setAuthOpen] = useState(false);
  const [dash, setDash] = useState(null);
  const [noOrg, setNoOrg] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await authFetch("/api/org/dashboard");
    if (res.status === 401) {
      clearAuth();
      setUser(null);
      return;
    }
    if (res.status === 400) {
      setNoOrg(true);
      setDash(null);
      return;
    }
    setNoOrg(false);
    setDash(await res.json());
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await authFetch("/api/org/create", { method: "POST", body: JSON.stringify({ name: orgName }) });
    const d = await res.json();
    if (!res.ok) toast.error(typeof d.detail === "string" ? d.detail : tui.createFail);
    else {
      toast.success(tui.created(d.org.code));
      await load();
    }
    setBusy(false);
  };

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await authFetch("/api/org/join", { method: "POST", body: JSON.stringify({ code: joinCode }) });
    const d = await res.json();
    if (!res.ok) toast.error(typeof d.detail === "string" ? d.detail : tui.joinFail);
    else {
      toast.success(tui.joined(d.org.name));
      await load();
    }
    setBusy(false);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(dash.org.code);
      toast.success(tui.copied);
    } catch {
      toast.error(tui.copyFail);
    }
  };

  const withReports = dash?.members.filter((m) => m.report) || [];
  const personaCounts = withReports.reduce((acc, m) => {
    acc[m.report.persona] = (acc[m.report.persona] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="team-page">
      <header className="max-w-5xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="team-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span> <span className="text-ink/35 font-body text-xs font-semibold tracking-[0.2em] uppercase ml-1">Teams</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <LangToggle />
          {user && (
            <button onClick={() => { clearAuth(); setUser(null); setDash(null); setNoOrg(false); }} data-testid="team-logout-button" className="flex items-center gap-1.5 text-xs font-semibold text-ink/50 hover:text-iqpink transition-colors">
              <LogOut className="h-3.5 w-3.5" /> {tui.signOut}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-14">
        {!user && (
          <div className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="team-gate">
            <span className="inline-flex h-14 w-14 rounded-2xl bg-iqpurple/10 items-center justify-center"><Lock className="h-6 w-6 text-iqpurple" /></span>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-6">{tui.gateTitle}</h1>
            <button onClick={() => setAuthOpen(true)} data-testid="team-signin-button" className="mt-8 bg-ink text-paper font-semibold px-8 py-4 rounded-full hover:bg-iqblue transition-colors duration-300">
              {tui.gateBtn}
            </button>
          </div>
        )}

        {user && noOrg && (
          <div className="grid md:grid-cols-2 gap-5" data-testid="team-setup">
            <form onSubmit={create} className="rounded-3xl bg-cream border border-ink/10 p-8">
              <span className="inline-flex h-11 w-11 rounded-2xl bg-iqblue/10 items-center justify-center"><Building2 className="h-5 w-5 text-iqblue" /></span>
              <h2 className="font-display font-bold text-xl tracking-tight mt-4">{tui.createTitle}</h2>
              <p className="mt-2 text-sm text-ink/55 leading-relaxed">{tui.createSub}</p>
              <input data-testid="org-name-input" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder={tui.orgNamePh} required className={`${inputCls} mt-5`} />
              <button disabled={busy} data-testid="org-create-button" className="mt-4 w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-3.5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : tui.createBtn}
              </button>
            </form>
            <form onSubmit={join} className="rounded-3xl bg-cream border border-ink/10 p-8">
              <span className="inline-flex h-11 w-11 rounded-2xl bg-iqgreen/10 items-center justify-center"><Users className="h-5 w-5 text-iqgreen" /></span>
              <h2 className="font-display font-bold text-xl tracking-tight mt-4">{tui.joinTitle}</h2>
              <p className="mt-2 text-sm text-ink/55 leading-relaxed">{tui.joinSub}</p>
              <input data-testid="org-code-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="e.g. A1B2C3" required maxLength={8} className={`${inputCls} mt-5 tracking-[0.3em] font-mono uppercase`} />
              <button disabled={busy} data-testid="org-join-button" className="mt-4 w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-3.5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : tui.joinBtn}
              </button>
            </form>
          </div>
        )}

        {user && !noOrg && !dash && <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-iqblue" data-testid="team-loading" /></div>}

        {user && dash && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight" data-testid="team-org-name">{dash.org.name}</h1>
                <p className="mt-2 text-sm text-ink/55">{tui.members(dash.members.length)} · {tui.proReports(withReports.length)}</p>
              </div>
              <button onClick={copyCode} data-testid="org-copy-code" className="flex items-center gap-2 border border-ink/20 rounded-full px-5 py-3 text-sm font-semibold hover:bg-ink hover:text-paper transition-colors duration-300">
                <Copy className="h-4 w-4" /> {tui.inviteCode} <span className="font-mono tracking-[0.2em]">{dash.org.code}</span>
              </button>
            </div>

            {Object.keys(personaCounts).length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="team-persona-mix">
                {Object.entries(personaCounts).map(([k, n]) => (
                  <span key={k} className="flex items-center gap-2 text-xs font-semibold rounded-full border px-4 py-2" style={{ borderColor: `${PERSONA_META[k].color}55`, color: PERSONA_META[k].color }}>
                    {PERSONA_META[k][lang]?.name || PERSONA_META[k].en.name} × {n}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-8 rounded-3xl bg-cream border border-ink/10 overflow-hidden" data-testid="team-table">
              <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-sm tracking-tight">{tui.cohort}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-ink/45 uppercase tracking-wider">
                      {tui.cols.map((c) => <th key={c} className="px-6 py-3 font-semibold">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {dash.members.map((m, i) => (
                      <tr key={i} className="border-t border-ink/5 hover:bg-white/60 transition-colors" data-testid={`team-member-${i}`}>
                        <td className="px-6 py-3.5">
                          <div className="font-semibold text-ink/85">{m.name} {m.role === "owner" && <span className="ml-1 text-[9px] font-bold uppercase tracking-wider text-iqpurple">{tui.owner}</span>}</div>
                          <div className="text-ink/45">{m.email}</div>
                        </td>
                        <td className="px-6 py-3.5 text-ink/70">{m.report?.industry || m.industry || "—"}</td>
                        <td className="px-6 py-3.5">
                          {m.report ? (
                            <span className="font-semibold" style={{ color: PERSONA_META[m.report.persona].color }}>
                              {PERSONA_META[m.report.persona][lang]?.name || PERSONA_META[m.report.persona].en.name}
                            </span>
                          ) : <span className="text-ink/40">{tui.notTaken}</span>}
                        </td>
                        <td className="px-6 py-3.5 font-bold text-ink/80">{m.report ? `${m.report.aptPct}%` : "—"}</td>
                        <td className="px-6 py-3.5 text-ink/55">{m.report ? new Date(m.report.date).toLocaleDateString(tui.dateLocale, { day: "2-digit", month: "short" }) : "—"}</td>
                        <td className="px-6 py-3.5">
                          {m.report && <Link to={`/r/${m.report.reportId || ""}`} className="text-iqblue font-semibold hover:underline" data-testid={`team-card-link-${i}`}>{tui.view}</Link>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={(u) => setUser(u)} />
    </div>
  );
}
